import { useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Button, Image, Input, Spinner
} from '@chakra-ui/react';
import { CHROME_LABEL } from '../../components/adChrome';
import { funnelStageLabel } from '../../components/adLabels';
import type { ShowcaseAd, ShowcaseProduct, ShowcaseViewProps } from './types';
import { wallOrder } from './wall';
import { resolveShowcaseChrome, ShowcaseAdMedia, ShowcaseChromeFrame } from './chrome';
import { AdShowcaseModal } from './AdShowcaseModal';
import { ConfigureChromePanel } from './ConfigureChromePanel';
import { safeHttpUrl } from '../../lib/safeHref';

const CARD_W: Record<ShowcaseAd['ratio'], string> = {
  '9:16': '236px', '4:5': '292px', '1:1': '304px', '1.91:1': '392px', '16:9': '392px'
};
const STAGE_COLOR: Record<string, string> = {
  awareness: '#16B8D8', consideration: '#A7E51F', conversion: '#D9008D'
};

type PlatformFilter = 'all' | 'meta' | 'google';
type StageFilter = 'all' | 'awareness' | 'consideration' | 'conversion';

const PLATFORM_CHIPS: Array<{ val: PlatformFilter; label: string }> = [
  { val: 'all', label: 'All placements' },
  { val: 'meta', label: 'Meta' },
  { val: 'google', label: 'Google PMax' }
];
const STAGE_CHIPS: Array<{ val: StageFilter; label: string }> = [
  { val: 'all', label: 'All stages' },
  { val: 'awareness', label: 'Awareness' },
  { val: 'consideration', label: 'Consideration' },
  { val: 'conversion', label: 'Conversion' }
];

export function ShowcaseView(props: ShowcaseViewProps) {
  const {
    title, cfg, overrides, view, surround, products, ads,
    isSnapshot, canConfigure, frozenAt,
    onTitleChange, onCfgChange, onOverrideChange, onViewChange, onSurroundChange,
    onShare, shareState
  } = props;

  const dark = surround === 'dark';
  const bg = dark ? '#08090C' : 'brand.canvas';
  const shell = dark ? '#0B0D12' : 'white';
  const panel = dark ? '#12141A' : 'white';
  const fg = dark ? '#F5F7FA' : 'brand.ink';
  const fg2 = dark ? '#A8B0BE' : 'brand.muted';
  const fg3 = dark ? '#6C7484' : 'gray.500';
  const bd = dark ? 'whiteAlpha.200' : 'brand.border';

  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [stage, setStage] = useState<StageFilter>('all');
  const [configOpen, setConfigOpen] = useState(false);
  const [modalId, setModalId] = useState<string | null>(null);
  const [railOpen, setRailOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const recommended = useMemo(() => ({ ...cfg }), []); // seeded snapshot at mount

  useEffect(() => {
    if (!shareState?.url) return;
    void navigator.clipboard.writeText(shareState.url).catch(() => { /* ignore */ });
    setCopied(true);
    const t = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(t);
  }, [shareState?.url]);

  const visibleAds = useMemo(() => ads.filter(a =>
    (platform === 'all' || a.family === platform) &&
    (stage === 'all' || a.stage === stage)
  ), [ads, platform, stage]);

  const groups = useMemo(() => {
    return products.map(p => ({
      product: p,
      ads: visibleAds.filter(a => a.product.id === p.id)
    })).filter(g => g.ads.length > 0);
  }, [products, visibleAds]);

  const wallAds = useMemo(
    () => wallOrder(visibleAds, products),
    [visibleAds, products]
  );

  const stats = useMemo(() => {
    const families = new Set(ads.map(a => a.family));
    const ratios = new Set(ads.map(a => a.ratio));
    return [
      { value: String(ads.length), label: 'Ads' },
      { value: String(products.length), label: 'Products' },
      { value: String(families.size), label: 'Platforms' },
      { value: String(ratios.size), label: 'Ratios' }
    ];
  }, [ads, products]);

  const modalAd = modalId ? (ads.find(a => a.id === modalId) ?? visibleAds.find(a => a.id === modalId) ?? null) : null;

  const shareLabel = shareState?.sharing ? 'Sharing…' : (copied ? 'Link copied' : 'Share');

  function handleShare() {
    if (shareState?.sharing) return;
    if (onShare) {
      onShare();
      return;
    }
    void navigator.clipboard.writeText(window.location.href).then(
      () => setCopied(true),
      () => { /* ignore */ }
    );
    window.setTimeout(() => setCopied(false), 2200);
  }

  const frozenLabel = frozenAt
    ? `Snapshot · ${new Date(frozenAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`
    : 'Snapshot';

  return (
    <Box minH="100%" bg={bg} color={fg}>
      <Flex
        position="sticky"
        top={0}
        zIndex={40}
        align="center"
        flexWrap="wrap"
        gap={3}
        px={7}
        py={3}
        minH="64px"
        bg={shell}
        borderBottomWidth="1px"
        borderColor={bd}
      >
        <HStack spacing={3} minW={0}>
          <BrandMark cfgLogo={cfg.logoUrl} name={cfg.brandName} size="28px" radius="8px" />
          <Text fontSize="13px" fontWeight="600" letterSpacing="-0.01em" noOfLines={1}>
            {cfg.brandName}
          </Text>
        </HStack>
        <Box w="1px" h="22px" bg={bd} display={{ base: 'none', md: 'block' }} />
        {canConfigure && onTitleChange ? (
          <Input
            value={title}
            onChange={e => onTitleChange(e.target.value)}
            variant="unstyled"
            fontSize="13px"
            fontWeight="600"
            maxW="320px"
            placeholder="Untitled showcase"
            _placeholder={{ color: fg3 }}
          />
        ) : (
          <Text fontSize="13px" fontWeight="600" noOfLines={1}>{title}</Text>
        )}
        {isSnapshot && (
          <HStack
            spacing={2}
            bg={dark ? 'whiteAlpha.100' : 'gray.100'}
            borderWidth="1px"
            borderColor={bd}
            borderRadius="full"
            px={3}
            py={1}
            fontSize="12px"
            color={fg2}
          >
            <Text>{frozenLabel}</Text>
          </HStack>
        )}
        <HStack ml="auto" spacing={2} flexWrap="wrap" justify="flex-end">
          <Button
            size="sm"
            variant="outline"
            borderColor={bd}
            color={fg2}
            onClick={() => onSurroundChange?.(dark ? 'light' : 'dark')}
          >
            {dark ? 'Light' : 'Dark'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            borderColor={bd}
            color={fg2}
            onClick={handleShare}
            isDisabled={!!shareState?.sharing}
            leftIcon={shareState?.sharing ? <Spinner size="xs" /> : undefined}
          >
            {shareLabel}
          </Button>
          {canConfigure && (
            <Button size="sm" variant="brand" onClick={() => setConfigOpen(true)}>
              Configure chrome
            </Button>
          )}
        </HStack>
      </Flex>

      {shareState?.error && (
        <Box px={7} pt={3}>
          <Text fontSize="sm" color="red.400">{shareState.error}</Text>
        </Box>
      )}

      <Flex align="flex-end" gap={7} px={7} pt={8} pb={5} flexWrap="wrap">
        <HStack spacing={4}>
          <BrandMark cfgLogo={cfg.logoUrl} name={cfg.brandName} size="52px" radius="14px" />
          <Box>
            <Text fontSize="28px" fontWeight="700" letterSpacing="-0.03em" lineHeight="1.1" color={fg}>
              {cfg.brandName}
            </Text>
            {cfg.handle && (
              <Text mt="5px" fontSize="12.5px" color={fg2}>@{cfg.handle.replace(/^@/, '')}</Text>
            )}
          </Box>
        </HStack>
        <HStack spacing={7} ml="auto" flexWrap="wrap">
          {stats.map(s => (
            <Box key={s.label}>
              <Text fontSize="20px" fontWeight="700" letterSpacing="-0.02em">{s.value}</Text>
              <Text mt="3px" fontSize="10.5px" fontWeight="600" letterSpacing="0.1em" textTransform="uppercase" color={fg3}>
                {s.label}
              </Text>
            </Box>
          ))}
        </HStack>
      </Flex>

      <Flex align="center" gap={2.5} px={7} pb={6} flexWrap="wrap">
        {PLATFORM_CHIPS.map(c => (
          <FilterChip
            key={`p-${c.val}`}
            label={c.label}
            active={platform === c.val}
            onClick={() => setPlatform(c.val)}
            dark={dark}
          />
        ))}
        {STAGE_CHIPS.map(c => (
          <FilterChip
            key={`s-${c.val}`}
            label={c.label}
            active={stage === c.val}
            onClick={() => setStage(c.val)}
            dark={dark}
          />
        ))}
        <HStack ml="auto" spacing={3.5}>
          <Text fontSize="12px" color={fg3}>
            {visibleAds.length === ads.length
              ? `Showing all ${ads.length} ads`
              : `Showing ${visibleAds.length} of ${ads.length} ads`}
          </Text>
          <HStack spacing="2px" bg={panel} borderWidth="1px" borderColor={bd} borderRadius="9px" p="2px">
            {(['rows', 'wall'] as const).map(v => {
              const on = view === v;
              return (
                <Button
                  key={v}
                  size="xs"
                  variant="ghost"
                  bg={on ? (dark ? 'whiteAlpha.100' : 'gray.100') : 'transparent'}
                  color={on ? fg : fg3}
                  onClick={() => onViewChange?.(v)}
                  borderRadius="7px"
                  px={3}
                >
                  {v === 'rows' ? 'By product' : 'Wall'}
                </Button>
              );
            })}
          </HStack>
        </HStack>
      </Flex>

      <Box px={7} pb={20}>
        {view === 'rows' ? (
          <VStack align="stretch" spacing={12}>
            {groups.map(g => (
              <ProductRow
                key={g.product.id}
                product={g.product}
                ads={g.ads}
                cfg={cfg}
                overrides={overrides}
                dark={dark}
                fg={fg}
                fg2={fg2}
                fg3={fg3}
                bd={bd}
                panel={panel}
                onOpen={setModalId}
              />
            ))}
            {groups.length === 0 && (
              <Text fontSize="sm" color={fg3}>No ads match the current filters.</Text>
            )}
          </VStack>
        ) : (
          <Box sx={{ columns: '4 260px', columnGap: '20px' }}>
            {wallAds.map(ad => (
              <Box
                key={ad.id}
                onClick={() => setModalId(ad.id)}
                sx={{ breakInside: 'avoid' }}
                mb={5}
                cursor="pointer"
              >
                <Box
                  borderRadius="10px"
                  overflow="hidden"
                  bg={panel}
                  borderWidth="1px"
                  borderColor={bd}
                  transition="transform 0.18s, border-color 0.18s"
                  _hover={{ transform: 'translateY(-3px)', borderColor: dark ? 'whiteAlpha.400' : 'gray.300' }}
                >
                  <ShowcaseChromeFrame ad={ad} cfg={cfg} overrides={overrides} density="full">
                    <ShowcaseAdMedia ad={ad} mode="tile" />
                  </ShowcaseChromeFrame>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <HStack spacing={2.5} fontSize="11.5px" color={fg3} pt={8}>
          <Image src="/reach-social-logo.png" alt="" h="16px" w="16px" opacity={0.75} />
          <Text>Generated by Reach Social</Text>
        </HStack>
      </Box>

      {canConfigure && configOpen && onCfgChange && (
        <ConfigureChromePanel
          cfg={cfg}
          recommended={recommended}
          onCfgChange={onCfgChange}
          onClose={() => setConfigOpen(false)}
          surround={surround}
        />
      )}

      {modalAd && (
        <AdShowcaseModal
          ad={modalAd}
          ads={visibleAds}
          allAds={ads}
          cfg={cfg}
          overrides={overrides}
          surround={surround}
          railOpen={railOpen}
          onRailChange={setRailOpen}
          onClose={() => setModalId(null)}
          onSelect={setModalId}
          onOverrideChange={canConfigure ? onOverrideChange : undefined}
        />
      )}
    </Box>
  );
}

function FilterChip({
  label, active, onClick, dark
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  dark: boolean;
}) {
  return (
    <Button
      size="xs"
      borderRadius="full"
      bg={active ? 'rsViolet.500' : 'transparent'}
      color={active ? 'white' : (dark ? '#A8B0BE' : 'brand.muted')}
      borderWidth="1px"
      borderColor={active ? 'rsViolet.500' : (dark ? 'whiteAlpha.200' : 'brand.border')}
      fontWeight="600"
      px={3}
      onClick={onClick}
      _hover={{ bg: active ? 'rsViolet.500' : (dark ? 'whiteAlpha.100' : 'gray.50') }}
    >
      {label}
    </Button>
  );
}

function BrandMark({
  cfgLogo, name, size, radius
}: {
  cfgLogo: string | null;
  name: string;
  size: string;
  radius: string;
}) {
  const initial = (name || 'B').slice(0, 1).toUpperCase();
  const logoSrc = safeHttpUrl(cfgLogo);
  return (
    <Flex
      w={size}
      h={size}
      flex="none"
      borderRadius={radius}
      overflow="hidden"
      align="center"
      justify="center"
      bgGradient="linear(140deg, #0b2b3f, #12303d)"
      borderWidth="1px"
      borderColor="whiteAlpha.200"
      color="#8FD3E8"
      fontSize="20px"
      fontWeight="700"
    >
      {logoSrc ? (
        <Image src={logoSrc} alt={name} w="100%" h="100%" objectFit="cover" />
      ) : (
        <Text fontSize="inherit" fontWeight="700">{initial}</Text>
      )}
    </Flex>
  );
}

function ProductRow({
  product, ads, cfg, overrides, dark, fg, fg2, fg3, bd, panel, onOpen
}: {
  product: ShowcaseProduct;
  ads: ShowcaseAd[];
  cfg: ShowcaseViewProps['cfg'];
  overrides: Record<string, string>;
  dark: boolean;
  fg: string;
  fg2: string;
  fg3: string;
  bd: string;
  panel: string;
  onOpen: (id: string) => void;
}) {
  return (
    <Box as="section">
      <Flex align="center" gap={3.5} pb={3.5} mb={5} borderBottomWidth="1px" borderColor={bd}>
        <Box
          w="38px" h="38px" flex="none" borderRadius="10px"
          bgGradient="linear(140deg, #0b2b3f, #12303d)"
          borderWidth="1px" borderColor={bd}
        />
        <Box minW={0}>
          <HStack spacing={2.5} align="baseline">
            <Text fontSize="16px" fontWeight="600" letterSpacing="-0.02em" color={fg} noOfLines={1}>
              {product.name || 'Untitled product'}
            </Text>
          </HStack>
          {product.sku && (
            <Text mt="3px" fontSize="11px" color={fg3} fontFamily="mono">{product.sku}</Text>
          )}
        </Box>
        <Text ml="auto" flex="none" fontSize="11px" color={fg3}>
          {ads.length} {ads.length === 1 ? 'ad' : 'ads'}
        </Text>
      </Flex>
      <Flex gap={4} align="flex-start" overflowX="auto" pb={3}>
        {ads.map(ad => (
          <AdTile
            key={ad.id}
            ad={ad}
            cfg={cfg}
            overrides={overrides}
            dark={dark}
            fg={fg}
            fg3={fg3}
            fg2={fg2}
            bd={bd}
            panel={panel}
            onOpen={onOpen}
          />
        ))}
      </Flex>
    </Box>
  );
}

function AdTile({
  ad, cfg, overrides, dark, fg, fg2, fg3, bd, panel, onOpen
}: {
  ad: ShowcaseAd;
  cfg: ShowcaseViewProps['cfg'];
  overrides: Record<string, string>;
  dark: boolean;
  fg: string;
  fg2: string;
  fg3: string;
  bd: string;
  panel: string;
  onOpen: (id: string) => void;
}) {
  const chrome = resolveShowcaseChrome(ad, overrides);
  const stageLabel = funnelStageLabel(ad.stage);
  return (
    <Box flex="none" w={CARD_W[ad.ratio]} cursor="pointer" onClick={() => onOpen(ad.id)}>
      <Box
        borderRadius="10px"
        overflow="hidden"
        bg={panel}
        borderWidth="1px"
        borderColor={bd}
        transition="transform 0.18s, border-color 0.18s"
        _hover={{ transform: 'translateY(-3px)', borderColor: dark ? 'whiteAlpha.400' : 'gray.300' }}
      >
        <ShowcaseChromeFrame ad={ad} cfg={cfg} overrides={overrides} density="full">
          <ShowcaseAdMedia ad={ad} mode="tile" />
        </ShowcaseChromeFrame>
      </Box>
      <HStack spacing={2} mt={2} px="2px" align="baseline">
        <Text fontSize="12px" fontWeight="600" letterSpacing="-0.01em" color={fg} noOfLines={1}>
          {CHROME_LABEL[chrome]}
        </Text>
        <Text fontSize="11px" color={fg3}>{ad.ratio}</Text>
        {ad.mediaKind === 'video' && <Text fontSize="11px" color={fg3}>Video</Text>}
        <Text ml="auto" fontSize="10px" color={fg3} fontFamily="mono" noOfLines={1}>
          {ad.platformFormat}
        </Text>
      </HStack>
      {stageLabel && (
        <HStack spacing={1.5} mt={1} px="2px">
          <Box w="6px" h="6px" borderRadius="full" bg={STAGE_COLOR[ad.stage || ''] || fg2} flex="none" />
          <Text fontSize="11px" fontWeight="500" color={fg2}>{stageLabel}</Text>
        </HStack>
      )}
    </Box>
  );
}
